import { FC, ReactNode, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { RenderIf } from 'components/RenderIf'
import { Sidebar } from 'featureSliced/widgets/Sidebar'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { getNavLink } from 'featureSliced/shared/utils/getNavLink'

import chevronLeft from 'assets/chevronLeft.svg'
import menuIcon from 'assets/meatballs.svg'
import clickAndCollectLogo from 'assets/prompt/clickAndCollect.svg'
import qrMenuLogo from 'assets/prompt/qrMenu.svg'

import { WebOrderSummary } from './WebOrderSummary'
import {
  WebAction,
  WebActions,
  WebBackLink,
  WebCheckoutBody,
  WebCheckoutForm,
  WebCheckoutInner,
  WebCheckoutTitle,
  WebHeaderBar,
  WebHeaderSpacer,
  WebLogo,
  WebLogoButton,
  WebScreen,
} from './styled'

type CheckoutWebProps = {
  /** the active step of the existing wizard, rendered as the form column */
  children: ReactNode
  /** hide the recap once the order is placed — the result screen stands alone */
  withSummary?: boolean
  title?: string
}

/**
 * QRM-641: the phone checkout is a four-step wizard. On the web layout the same
 * step components sit in one page with the order recap fixed beside them, and the
 * cart-review step is dropped — the cart is the column on the menu page.
 *
 * Deliberately a shell only: no payment logic, no validation and no payload
 * building moved in here.
 */
export const CheckoutWeb: FC<CheckoutWebProps> = ({
  children,
  withSummary = true,
  title,
}) => {
  const t = useTranslations()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { logoImage } = useMySelector((state) => state.app.interfaceStyles)
  const { CompanyName } = useMySelector((state) => state.app.companyData)

  const [isSidebarShowing, setIsSidebarShowing] = useState(false)

  const backToMenu = () => navigate(getNavLink(searchParams, 'menu'))

  const fallbackLogo =
    process.env.REACT_APP_IS_CLIC === '1' ? clickAndCollectLogo : qrMenuLogo

  return (
    <WebScreen data-testid="checkout-web">
      <Sidebar
        isSidebarShowing={isSidebarShowing}
        setIsSidebarShowing={setIsSidebarShowing}
        openProfileModal={() => undefined}
        openAuthModal={() => undefined}
      />

      <WebHeaderBar>
        <WebLogoButton type="button" onClick={() => navigate('/')}>
          <WebLogo src={logoImage || fallbackLogo} alt={CompanyName} />
        </WebLogoButton>
        <WebHeaderSpacer />
        <WebActions>
          <WebAction
            type="button"
            onClick={() => setIsSidebarShowing(true)}
            aria-label={t('cover.menu')}
          >
            <img src={menuIcon} alt="" width="20" height="20" />
          </WebAction>
        </WebActions>
      </WebHeaderBar>

      <WebCheckoutBody>
        <WebCheckoutInner>
          <WebBackLink type="button" onClick={backToMenu}>
            <img src={chevronLeft} alt="" width="18" height="18" />
            <span>{t('buttons.goBack')}</span>
          </WebBackLink>

          <RenderIf condition={!!title}>
            <WebCheckoutTitle>{title}</WebCheckoutTitle>
          </RenderIf>

          <WebCheckoutForm withSummary={withSummary}>
            <div>{children}</div>
            <RenderIf condition={withSummary}>
              <WebOrderSummary onEdit={backToMenu} />
            </RenderIf>
          </WebCheckoutForm>
        </WebCheckoutInner>
      </WebCheckoutBody>
    </WebScreen>
  )
}
